import { Instant } from "@js-joda/core";
import { useGetProductTemplateConfig, useGetProductTemplateData } from "api/api-hooks/product-templates-hooks";
import { OptionalFilter } from "components/ComponentTypeTypes";
import ProductTemplateFilter from "components/dashboard/product-template-table/components/ProductTemplateFilter";
import { IProductTemplate, ITableDataEntry } from "components/dashboard/product-template-table/utils/interfaces";
import {
  optionalFilterOptionsFunction,
  promiseHandlerWithControlledColumns,
} from "components/dashboard/product-template-table/utils/utils";
import { Dropdown } from "primereact/dropdown";
import { MultiSelect } from "primereact/multiselect";
import React, { useCallback, useEffect, useId, useState } from "react";
import { APIResponse } from "services/HttpService";
import { T } from "services/lang/LanguageService";

interface IProductTemplateEntriesDropdownProps {
  safeName: string;
  dateFrom: Instant;
  startAttribute: string;
  endAttribute: string;
  setProductItemsDateRange?: (value: string[]) => void;
  showFilter?: boolean;
  defaultFilter?: string; // filter to apply by default
  filter?: string; // filter to apply that is not visible for the user
  optionalFilters?: OptionalFilter[]; // filters to optionally apply
}

const ProductTemplateEntriesDropdown = (props: IProductTemplateEntriesDropdownProps) => {
  const [allSelections, setAllSelections] = useState<{ label: string; value: string }[]>([]);
  const [selectedItem, setSelectedItem] = useState<string | null>(null);
  const [filter, setFilter] = useState<string | null | undefined>(props.filter);
  const [selectedOptionalFilters, setSelectedOptionalFilters] = useState<OptionalFilter[]>([]);

  const id = useId();

  const currentFilter =
    selectedOptionalFilters.length > 0
      ? selectedOptionalFilters.map(f => f.filter).join(";")
      : filter?.trim() || props.defaultFilter;

  const {
    data: templateData,
    isError: templateError,
    isSuccess: templateSuccess,
    isLoading: isLoadingTemplate,
  } = useGetProductTemplateData(
    {
      safeName: props.safeName,
      dateFrom: props.dateFrom.toString(),
      filter: currentFilter,
    },
    {
      enabled: !!props.safeName && !!props.dateFrom,
    }
  );

  const { data: configData } = useGetProductTemplateConfig(props.safeName, id, [], [], {
    usePromiseHandler: (res: APIResponse<IProductTemplate>) => promiseHandlerWithControlledColumns(res),
  });

  useEffect(() => {
    if (templateSuccess && templateData) {
      const { startAttribute } = props;
      const selections = templateData.reduce((acc: { label: string; value: string }[], entry: ITableDataEntry) => {
        if (entry.entries[startAttribute]?.value) {
          acc.push({
            label: entry.stringRepresentation ?? "",
            value: entry.id ?? "",
          });
        }
        return acc;
      }, []);

      setAllSelections(selections);

      if (selections.length > 0 && !selections.find(s => s.value === selectedItem)) {
        onChange(selections[0].value);
      }
    }
  }, [templateSuccess, templateData]);

  const errorText = templateError ? T("error.product-template-selection-error") : null;
  const loadingText = isLoadingTemplate ? T("message.product-templates-loading") : null;

  const placeholderText = errorText || loadingText || T("message.select-product-template");

  const handleFilterApply = useCallback(
    (filterLine: string) => {
      setFilter(filterLine ?? "");
    },
    [props]
  );

  const onChange = (value: string) => {
    const { startAttribute, endAttribute } = props;
    const part = templateData?.find((data: ITableDataEntry) => data.id === value);

    if (!part) return;

    const startDate = part.entries[startAttribute]?.value as string;
    const endDate = endAttribute ? (part.entries[endAttribute]?.value as string) : Instant.now().toString();

    props.setProductItemsDateRange?.([startDate, endDate]);

    setSelectedItem(value);
  };

  const selectedTemplate = (option: { label: string; value: string }) =>
    option?.value ? <span>{option.label}</span> : <span>{placeholderText}</span>;

  const itemTemplate = (option: { label: string; value: string }) => <span>{option.label}</span>;

  return (
    <div className="flex flex-row gap-2">
      {templateError && <span>{T("error.product-template-selection-error")}</span>}

      {isLoadingTemplate && !templateError && <span>{T("message.product-templates-loading")}</span>}

      {templateSuccess && (
        <Dropdown
          placeholder={placeholderText}
          value={selectedItem}
          options={allSelections}
          className="h-max"
          filter
          filterMatchMode="contains"
          onChange={e => onChange(e.value)}
          valueTemplate={selectedTemplate}
          itemTemplate={itemTemplate}
        />
      )}

      {props.showFilter && (
        <ProductTemplateFilter
          defaultFilter={props.defaultFilter}
          secondaryFilter={props.filter}
          onApplyFilter={handleFilterApply}
          attributes={configData?.data?.attributes ?? []}
        />
      )}

      {props.optionalFilters && (
        <MultiSelect
          value={selectedOptionalFilters}
          options={optionalFilterOptionsFunction(props.optionalFilters)}
          optionLabel="label"
          placeholder={T("label.optional-filters")}
          onChange={e => setSelectedOptionalFilters(e.value)}
          className="w-15rem"
        />
      )}
    </div>
  );
};

export default ProductTemplateEntriesDropdown;
